/**
 * Navigation Category Map
 * Maps navigation categories to groups of routes.
 * Routes within the same category open in the same tab.
 * Routes in different categories open in a new tab.
 */
export const NavCategoryMap = {
  0: ["", "/"],
  1: [
    "/providers/list",
    "/providers/unassigned-providers",
    "/providers/state-license",
    "/providers/providerProfile",
    "/providers/edit-provider-profile",
    "/providers/capacity-input",
    "/providers/custom-view",
    "/providers/ProviderCustomView",
    "/providers/custom-view/config-custom-view",
    "/providers/report",
    "/providers/audit-logs",
    "/providers/site-enterprise",
    "/providers/hourly-volume-share",
    "/providers/schedule-enterprise",
    "/providers/leadership-bulk-update",
    "/providers/annual-hour-tracker",
  ],
  2: [
    "/facilities/list",
    "/facilities/facilityprofile",
    "/facilities/edit-facility-profile",
    "/facilities/unassigned-facilities",
    "/facilities/FacilityAliasManagement",
    "/facilities/hourly-volume-share",
  ],
  3: [
    "/admin/region",
    "/admin/rpce",
    "/admin/worklist",
    "/admin/practice",
    "/admin/subpractice",
    "/admin/user-management",
    "/admin/sectionworklist",
    "/admin/configuration/FacilityCoverageMatrix",
    "/admin/configuration/PrioritySLAMapping",
    "/admin/configuration/day-of-week-configuration",
    "/admin/configuration/DLSConfiguration",
    "/admin/configuration/TaskConfiguration",
    "/admin/configuration/coverage-gap",
    "/admin/configuration/simulator-color-configuration",
    "/admin/configuration/block-planning",
    "/admin/configuration/ShiftHourConfiguration",
    "/admin/holiday-planner",
    "/admin/holiday-demand-planner",
    "/admin/provider-type",
    "/admin/provider-status",
    "/admin/dropdown-management/provider-communication-type",
    "/admin/dropdown-management/scheduletype",
    "/admin/dropdown-management/subspeciality",
    "/admin/dropdown-management/billing-management",
    "/admin/dropdown-management/week-rotation",
    "/admin/dropdown-management/week-rotation-template",
    "/admin/dropdown-management/partnership-rate",
    "/admin/dropdown-management/partnership-status",
    "/admin/dropdown-management/time-to-partnership",
    "/admin/dropdown-management/providerdegree",
    "/admin/dropdown-management/committees",
    "/admin/dropdown-management/future-section-change",
    "/admin/dropdown-management/rvurate",
    "/admin/dropdown-management/timezone-management",
    "/admin/dropdown-management/providerrequest",
    "/admin/dropdown-management/trauma-facility-level",
    "/admin/dropdown-management/encore-section",
    "/admin/dropdown-management/facility-status",
  ],
  4.1: ["/compensation-type"],
  4.2: ["/compensation-configuration"],
  4.3: ["/payroll-generation"],
  5: [
    "/exam/raw-exam-data",
    "/exam/exam-data",
    "/exam/miscellaneous-facility-data",
    "/exam/readdata",
    "/exam/unread",
    "/exam/TAT",
  ],
  6: [
    "/reports/matrix-daily-report",
    "/reports/dashboard",
    "/reports/coverage-gap-tool",
    "/reports/holiday-gap-tool",
    "/reports/report-configuration",
    "/reports/performance-dashboard",
    "/reports/privilege-power-sync",
  ],
  7.1: ["/strategic-forecast/strategic-demand-planner"],
  7.2: ["/strategic-forecast/strategic-capacity-planner"],
  7.3: ["/strategic-forecast/strategic-caps-vs-demand-planner"],
  7.4: ["/strategic-forecast/matrix-service-request"],
  8: ["/schedule"],
  9: ["/scheduling-forecast-reports"],
  10: ["/demand-table"],
  11: ["/capacity-table"],
  12: ["/capacity-vs-demand-table"],
  13: ["/provider-profile"],
  14: ["/live-available-shift"],
  15: ["/site-scheduling-view", "/site-task-view"],
  16: ["/tenant-management"],
  17: ["/volume-trend-report"],
};

/**
 * Facility schedule related paths for special handling
 */
export const siteSchedulePaths = ["/site-scheduling-view", "/site-task-view"];

export function getNavCategory(pathName = "") {
  // Normalize the path - remove trailing slashes and ensure it starts with /
  let normalizedPath = pathName.trim();
  if (normalizedPath !== "/" && normalizedPath.endsWith("/")) {
    normalizedPath = normalizedPath.slice(0, -1);
  }
  if (!normalizedPath.startsWith("/") && normalizedPath !== "") {
    normalizedPath = "/" + normalizedPath;
  }

  // Check each category
  for (let category in NavCategoryMap) {
    const paths = NavCategoryMap[category];
    // Check for exact match or if the current path starts with any of the category paths
    const isMatch = paths.some((path) => {
      if (path === normalizedPath) return true;
      // Handle home/root path specially
      if (path === "" || path === "/") {
        return normalizedPath === "" || normalizedPath === "/";
      }
      // Check if the current path is a sub-path of the category path
      return normalizedPath === path || normalizedPath.startsWith(path + "/");
    });

    if (isMatch) {
      return category;
    }
  }

  return null;
}

export function shouldOpenInNewTab(currentPath, targetPath) {
  const currentCategory = getNavCategory(currentPath);
  const targetCategory = getNavCategory(targetPath);

  // If categories are different, open in new tab
  return currentCategory !== targetCategory;
}

export function buildNavigationUrl(targetPath, options = {}) {
  const { currentPath, searchParams } = options;
  let finalPath = targetPath;

  // Special handling for site schedule paths
  if (currentPath && searchParams) {
    const isSiteSchedulePath = siteSchedulePaths.some(
      (p) => currentPath.toLowerCase() === p.toLowerCase(),
    );

    if (isSiteSchedulePath && targetPath === "/site-scheduling-view") {
      const urlParams = new URLSearchParams(searchParams);
      const siteCode = urlParams.get("siteCode");
      if (siteCode) {
        finalPath = `${targetPath}?siteCode=${encodeURIComponent(siteCode)}`;
      }
    }
  }

  return finalPath;
}
